import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

interface ConsentStatus {
  hasValidConsent: boolean;
  missingPurposes: string[];
  isExpired: boolean;
  lastChecked: Date;
  consentRecord?: {
    id: string;
    purposes_consented: string[];
    consent_timestamp: string;
    is_active: boolean;
  } | null;
}

interface ConsentContextType {
  consentStatus: ConsentStatus;
  isLoading: boolean;
  error: string | null;
  refreshConsentStatus: () => Promise<void>;
  checkPurposes: (requiredPurposes: string[]) => Promise<{
    hasValidConsent: boolean;
    missingPurposes: string[];
  }>;
}

const ConsentContext = createContext<ConsentContextType | undefined>(undefined);

export const useConsentContext = () => {
  const context = useContext(ConsentContext);
  if (!context) {
    throw new Error('useConsentContext must be used within a ConsentProvider');
  }
  return context;
};

interface ConsentProviderProps {
  children: ReactNode;
}

export const ConsentProvider: React.FC<ConsentProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [consentStatus, setConsentStatus] = useState<ConsentStatus>({
    hasValidConsent: false,
    missingPurposes: [],
    isExpired: false,
    lastChecked: new Date(),
    consentRecord: null
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isChecking, setIsChecking] = useState(false);

  const refreshConsentStatus = async () => {
    if (isChecking || !user) {
      return;
    }

    setIsChecking(true);
    setError(null);

    try {
      const { data: consents, error: consentError } = await supabase
        .from('patient_consents')
        .select('id, patient_id, purposes_consented, consent_timestamp, is_active, created_at')
        .eq('patient_id', user.id)
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(1);

      if (consentError) {
        console.error('Error fetching consent:', consentError);
        if (!consentError.code || consentError.code !== 'PGRST116') {
          setError(`Failed to verify consent status: ${consentError.message}`);
        }
        setConsentStatus({
          hasValidConsent: false,
          missingPurposes: [],
          isExpired: false,
          lastChecked: new Date(),
          consentRecord: null
        });
        return;
      }

      const latestConsent = consents?.[0];
      
      if (!latestConsent) {
        setConsentStatus({
          hasValidConsent: false,
          missingPurposes: [],
          isExpired: false,
          lastChecked: new Date(),
          consentRecord: null
        });
        return;
      }

      // Check if consent is expired
      const now = new Date();
      const consentDate = new Date(latestConsent.consent_timestamp);
      const daysDiff = (now.getTime() - consentDate.getTime()) / (1000 * 3600 * 24);
      const isExpired = daysDiff > 365; // 1 year expiry

      setConsentStatus({
        hasValidConsent: !isExpired,
        missingPurposes: [],
        isExpired,
        lastChecked: new Date(),
        consentRecord: !isExpired ? {
          id: latestConsent.id,
          purposes_consented: latestConsent.purposes_consented || [],
          consent_timestamp: latestConsent.consent_timestamp,
          is_active: latestConsent.is_active
        } : null
      });

    } catch (error) {
      console.error('Unexpected error checking consent:', error);
      setError('Unexpected error occurred while checking consent');
      setConsentStatus({
        hasValidConsent: false,
        missingPurposes: [],
        isExpired: false,
        lastChecked: new Date(),
        consentRecord: null
      });
    } finally {
      setIsChecking(false);
      setIsLoading(false);
    }
  };

  const checkPurposes = async (requiredPurposes: string[]): Promise<{
    hasValidConsent: boolean;
    missingPurposes: string[];
  }> => {
    try {
      console.log('🔍 checkPurposes DEBUG - Starting:', {
        requiredPurposes,
        userExists: !!user,
        userId: user?.id,
        userEmail: user?.email,
        timestamp: new Date().toISOString()
      });

      if (!user) {
        console.log('🔍 checkPurposes DEBUG - No user, returning false');
        return {
          hasValidConsent: false,
          missingPurposes: requiredPurposes
        };
      }

      // Get the purpose IDs for the required purpose codes
      console.log('🔍 checkPurposes DEBUG - About to query data_processing_purposes', {
        requiredPurposes,
        requiredPurposesType: typeof requiredPurposes,
        requiredPurposesLength: requiredPurposes?.length || 0,
        isArray: Array.isArray(requiredPurposes)
      });
      
      // Now the full query
      const { data: purposeMappings, error: purposeError } = await supabase
        .from('data_processing_purposes')
        .select('id, purpose_code')
        .in('purpose_code', requiredPurposes)
        .eq('is_active', true);

      console.log('🔍 checkPurposes DEBUG - Query result:', {
        data: purposeMappings,
        error: purposeError,
        dataCount: purposeMappings?.length || 0,
        errorDetails: purposeError ? {
          message: purposeError.message,
          details: purposeError.details,
          hint: purposeError.hint,
          code: purposeError.code
        } : null
      });

      if (purposeError || !purposeMappings || purposeMappings.length === 0) {
        console.error('🚨 checkPurposes ERROR - Purpose mappings failed:', {
          error: purposeError,
          mappings: purposeMappings,
          mappingsLength: purposeMappings?.length || 0,
          requiredPurposes
        });
        return {
          hasValidConsent: false,
          missingPurposes: requiredPurposes
        };
      }

      // Create a mapping of purpose_code to id
      const purposeCodeToIdMap = purposeMappings.reduce((map, purpose) => {
        map[purpose.purpose_code] = purpose.id;
        return map;
      }, {} as Record<string, string>);

      console.log('🔍 checkPurposes DEBUG - Purpose mapping created:', {
        purposeCodeToIdMap,
        purposeMappingsCount: purposeMappings.length,
        purposeMappings
      });

      // Get the IDs of required purposes
      const requiredPurposeIds = requiredPurposes
        .map(code => purposeCodeToIdMap[code])
        .filter(id => id !== undefined);

      console.log('🔍 checkPurposes DEBUG - Required purpose IDs:', {
        requiredPurposes,
        requiredPurposeIds,
        mappingWorked: requiredPurposeIds.length === requiredPurposes.length
      });

      // Get the user's current consent record from database
      const { data: userConsents, error: userConsentError } = await supabase
        .from('patient_consents')
        .select('id, purposes_consented, consent_timestamp, is_active')
        .eq('patient_id', user.id)
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(1);

      console.log('🔍 checkPurposes DEBUG - User consent query:', {
        userConsents,
        userConsentError,
        userConsentCount: userConsents?.length || 0
      });

      if (userConsentError || !userConsents || userConsents.length === 0) {
        console.log('🔍 checkPurposes DEBUG - No valid consent found');
        return {
          hasValidConsent: false,
          missingPurposes: requiredPurposes
        };
      }

      const userConsent = userConsents[0];
      const consentedPurposeIds = userConsent.purposes_consented || [];

      console.log('🔍 checkPurposes DEBUG - Consent status check:', {
        userConsent,
        consentedPurposeIds,
        hasConsentRecord: !!userConsent
      });

      // Find which required purpose IDs are missing from the consented IDs
      const missingPurposeIds = requiredPurposeIds.filter(
        purposeId => !consentedPurposeIds.includes(purposeId)
      );

      // Map missing IDs back to purpose codes for UI display
      const missingPurposes = missingPurposeIds.map(id => {
        // Find the original purpose code that matches this ID
        for (const code in purposeCodeToIdMap) {
          if (purposeCodeToIdMap[code] === id) {
            return code;
          }
        }
        return ''; // Fallback
      }).filter(code => code);

      console.log('🔍 checkPurposes DEBUG - Final validation result:', {
        requiredPurposeIds,
        consentedPurposeIds,
        missingPurposeIds,
        missingPurposes,
        hasValidConsent: missingPurposes.length === 0,
        shouldShowModal: missingPurposes.length > 0
      });

      return {
        hasValidConsent: missingPurposes.length === 0,
        missingPurposes
      };
    } catch (error) {
      console.error('Error checking purposes:', error);
      return {
        hasValidConsent: false,
        missingPurposes: requiredPurposes
      };
    }
  };

  useEffect(() => {
    if (user) {
      refreshConsentStatus();
    }
  }, [user]);

  return (
    <ConsentContext.Provider
      value={{
        consentStatus,
        isLoading,
        error,
        checkPurposes,
        refreshConsentStatus,
      }}
    >
      {children}
    </ConsentContext.Provider>
  );
};
